import {
  type GetVisitsRequestQuery,
  type GetVisitsWithCaseDataResponse,
} from "@clipboard-health/contract-worker-app-bff";
import { useWorkerPreferences } from "@src/appV2/Agents/api/useWorkerPreferences";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { useMemo } from "react";

import { type PatientData } from "./types";
import { useGetVisitsWithCaseData } from "./useGetVisitsWithCaseData";

export function useVisitsListData(
  bookedStatus: { booked: GetVisitsRequestQuery["filter"]["booked"] },
  options?: UseGetQueryOptions<GetVisitsWithCaseDataResponse>
) {
  const worker = useDefinedWorker();
  const { filters } = useWorkerPreferences();

  const { data: workerQualifications, isSuccess: workerQualificationsAreLoaded } =
    useWorkerQualifications();

  const queryParams: GetVisitsRequestQuery["filter"] = {
    booked: bookedStatus.booked,
    longitude: worker.geoLocation?.coordinates[0] ?? 0,
    latitude: worker.geoLocation?.coordinates[1] ?? 0,
    maxDistanceInMiles: filters?.distance,
    qualifications: workerQualificationsAreLoaded
      ? workerQualifications?.map((q) => q.attributes.name).join(",")
      : undefined,
  };

  const {
    data: visitsData,
    isLoading,
    isSuccess,
  } = useGetVisitsWithCaseData(queryParams, {
    ...options,
    enabled: workerQualificationsAreLoaded,
  });

  const {
    visits,
    caseIdToPatientMap,
    caseIdToSpecialtiesMap,
    caseIdToWorkplaceNameMap,
    caseIdToWorkplaceIdMap,
  } = useMemo(() => {
    const visitsList = visitsData?.data ?? [];
    const cases = visitsData?.included.filter((record) => record.type === "case") ?? [];
    const workplaces = visitsData?.included.filter((record) => record.type === "workplace") ?? [];

    const workplaceById = new Map(workplaces.map((w) => [w.id, w]));

    const patientMap = new Map<string, PatientData>();
    const specialtiesMap = new Map<string, string[]>();
    const workplaceNameMap = new Map<string, string>();
    const workplaceIdMap = new Map<string, string>();

    cases.forEach((caseData) => {
      patientMap.set(caseData.id, caseData.attributes.patient);
      specialtiesMap.set(caseData.id, caseData.attributes.specialties);
      workplaceNameMap.set(
        caseData.id,
        workplaceById.get(caseData.attributes.workplaceId)?.attributes.name ?? ""
      );
      workplaceIdMap.set(caseData.id, caseData.attributes.workplaceId);
    });

    return {
      visits: visitsList,
      caseIdToPatientMap: patientMap,
      caseIdToSpecialtiesMap: specialtiesMap,
      caseIdToWorkplaceNameMap: workplaceNameMap,
      caseIdToWorkplaceIdMap: workplaceIdMap,
    };
  }, [visitsData]);

  return {
    visits,
    isVisitsLoading: isLoading,
    isVisitsSuccess: isSuccess,
    caseIdToPatientMap,
    caseIdToSpecialtiesMap,
    caseIdToWorkplaceNameMap,
    caseIdToWorkplaceIdMap,
  };
}
